import type { AssistantRuntime } from '@assistant-ui/react';
import { useEffect } from 'react';

import {
  type ThreadHistoryAdapterWithSnapshot,
  withActiveBranchTail,
} from '#plugin-chat/lib/transport/threadHistoryAdapter.ts';

/**
 * The single persistence writer: on every thread change, persist the
 * runtime's full message tree atomically via
 * {@link ThreadHistoryAdapterWithSnapshot.snapshot} — the exported repository
 * overlaid with the active branch's trailing messages (see
 * {@link withActiveBranchTail}), so a streamed assistant turn that `export()`
 * drops as still-optimistic is never lost.
 *
 * Subscribing to repository *changes* — rather than to the vendored
 * `useExternalHistory`'s `isRunning` true→false transition — covers by
 * construction every state that previously needed its own write seam:
 *
 * - a **pending interrupt** (`askUserQuestion` awaiting `human()`) keeps the
 *   AI SDK status at `streaming`, so the vendored settle-persist never fires
 *   — but the message change that added the pending tool call fires this
 *   subscription;
 * - a **cancelled interrupt** settles the dangling tool call via
 *   `addToolOutput` without a new `isRunning` transition — again a message
 *   change;
 * - ordinary turns, edits, and regenerations are message changes too, and
 *   the persisted tree carries every branch with real parent links, so
 *   sibling branches can never be lost to active-branch reconstruction.
 *
 * The write is debounced by one macrotask (`setTimeout(0)`, the same shape
 * the vendored persist uses) so chunked stream updates collapse to one
 * snapshot per pause. A debounce pending at teardown is flushed synchronously
 * so a thread switch cannot drop the final state.
 *
 * `snapshot` is idempotent (same repository ⇒ same bytes) and internally
 * gated — it refuses to write while the thread has not hydrated from the
 * persisted document, so an early subscription firing before history load
 * completes can never overwrite good data.
 */
export function useThreadSnapshotPersistence({
  runtime,
  historyAdapter,
}: {
  runtime: AssistantRuntime;
  historyAdapter: ThreadHistoryAdapterWithSnapshot;
}): void {
  useEffect(() => {
    const state: { timer: ReturnType<typeof setTimeout> | undefined; pending: boolean } = {
      timer: undefined,
      pending: false,
    };
    const persist = (): void => {
      state.pending = false;
      // The full tree: the exported repository (every committed branch) overlaid
      // with the active branch's trailing messages. `export()` drops a streamed
      // assistant turn while it is still flagged `isOptimistic` — and that flag
      // outlives the run's `isRunning` window — so export alone persists only the
      // user turn. `getState().messages` (the active branch) always carries it;
      // `withActiveBranchTail` restores it without losing sibling branches.
      const repo = withActiveBranchTail(runtime.thread.export(), runtime.thread.getState().messages);
      historyAdapter.snapshot(repo);
    };
    const unsubscribe = runtime.thread.subscribe(() => {
      clearTimeout(state.timer);
      state.pending = true;
      state.timer = setTimeout(persist, 0);
    });
    return () => {
      unsubscribe();
      clearTimeout(state.timer);
      if (state.pending) {
        persist();
      }
    };
  }, [runtime, historyAdapter]);
}
